<template>
  <div class="license-update-dialog-wrap">
    <VueFinalModal v-if="showModel" v-bind="{
            name: 'license-update-dialog',
            clickToClose: false,
            drag: false,
            resize: false,
            contentStyle: {
              'width': '90%',
              'height': 'auto',
              'max-width': '800px',
            },
          }"
          v-model="showModel"
          overlay-class="overlay-class"
          classes="modal-container"
          content-class="modal-content">
        <div class="modal-header">
          <div class="close-wrap">
            <button class="close-button"
                    type="button"
                    @click="hide">
            </button>
          </div>
        </div>
        <div class="modal-body">
          <div class="account-name">
            <template v-if="fromLogin">ライセンスコードをお持ちの方へ</template>
          </div>
          <div class="license-update-caption">
            蔵衛門クラウドのご契約内容を更新します<br/>
          【ライセンスコード】を入力してください
          </div>
          <div class="license-input-instruction">
            ※ライセンスコードをメール等で受け取っている場合、16桁のコードをコピーして<br/>
            一番左の入力枠に貼り付けると、16桁まとめて入力できます
          </div>
          <div class="license-key-input-wrap" :class="{ fromLogin: fromLogin }">
            <LicenseCodeInput
              v-model="code1"
              :errorMessage="errors.code1"
              nameForValidation="code1"
              @paste.prevent="onPaste1"
              ref="licenseKey1"
              />
            <LicenseCodeInput
              v-model="code2"
              :errorMessage="errors.code2"
              nameForValidation="code2"
              ref="licenseKey2"
              />
            <LicenseCodeInput
              v-model="code3"
              :errorMessage="errors.code3"
              nameForValidation="code3"
              ref="licenseKey3"
              />
            <LicenseCodeInput
              v-model="code4"
              :errorMessage="errors.code4"
              nameForValidation="code4"
              ref="licenseKey4"
              />
            <div class="codes-error-message">
              <ValidationMessage :errorMessage="codesErrorMessage"></ValidationMessage>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="ok-button-wrap">
            <button type="button"
                    class="ok-button"
                    :disabled="!meta.valid"
                    @click="updateLicense">OK</button>
          </div>
          <template v-if="!fromLogin">
            <!-- コードを持って(いることが確定して)いない -->
            <div v-if="isFreePlan || isTrialPlan" class="buy-license-key-link-wrap">
              <!-- フリーまたはトライアル -->
              <button type="button" 
                      class="buy-license-key-link"
                      @click="() => { externalTransitionStore.doConfirm(externalLinks.purchase_license_trial.url) }"><!--
              -->ライセンスコードの購入はこちら</button>
            </div>
            <div v-else class="buy-license-key-wrap">
              <!-- プレミアム -->
              <LicensePromotionTextComponent/>
              <div class="buy-license-key-button-wrap">
                <button type="button" 
                        class="buy-license-key-button"
                        @click="() => { externalTransitionStore.doConfirm(externalLinks.premium_expiring_notification.url) }"><!--
                -->ライセンスコードを購入する</button>
              </div>
            </div>
          </template>
        </div>
    </VueFinalModal>
    <!-- ライセンス更新完了ダイアログ(成功した場合) -->
    <DialogPanel modal-name="license-update-complete-dialog"
                 :modalAttrs="{ maxWidth: 630 }"
                 ref="theLicenseUpdateCompleteDialog">
      <div class="license-update-complete-dialog-message">
        <div class="message">
          <span v-for="(line, idx) in resultMessage.split(`\n`)" :key="idx">{{ line }}<br/></span>
        </div>
        <div class="contract-wrap">
          <div class="contract-text">
            <div>
              <span class="plan-name-prefix">プラン：</span>
              <span class="plan-name">{{ acceptedPlan?.service_contract_type_name }}</span>
            </div>
            <div>
              <span class="contract-term-prefix">利用期間：</span>
              <span class="contract-term">{{ contractTerm }}</span>
            </div>
          </div>
        </div>
        <div class="contract-term-link-wrap">
          <a :href="externalLinks.license_period.url" target="_blank"
            class="contract-term-link">利用期間に関して詳しくはこちら</a>
        </div>
        <div class="button-wrap">
          <button type="button"
                  class="ok-button"
                  @click="licenseUpdateCompleted">
            OK
          </button>
        </div>
      </div>
    </DialogPanel>
    <!-- ライセンス更新エラーダイアログ -->
    <DialogPanel modal-name="license-update-error-dialog"
                 main-button-text="OK"
                 @main-button-click="closeWarningDialog"
                 :modalAttrs="{ maxWidth: 600 }"
                 ref="theLicenseUpdateErrorDialog">
      <div class="license-update-error-dialog-message">
        <div class="error-message">
          <span v-for="(line, idx) in errorMessage.message.split(`\n`)" :key="idx">{{ line }}<br/></span>
        </div>
      </div>
      <template v-if="errorMessage.displayContactSupportLink" v-slot:contactSupportLink>
        ご確認や購入については、<ContactSupportLinkComponent>お問い合わせフォーム</ContactSupportLinkComponent>よりご連絡ください
      </template>
      <template v-if="errorMessage.displayPurchaseLicenseLink" v-slot:footerText>
        <div class="footer-link-wrap">
          <button type="button" 
                  class="footer-buy-license-key-link"
                  @click="() => { 
                    const url = (isFreePlan || isTrialPlan) ? externalLinks.purchase_license_trial.url : externalLinks.premium_expiring_notification.url;
                    externalTransitionStore.doConfirm(url) 
                  }"><!--
          -->ライセンスコードの購入はこちら</button>
        </div>
      </template>
    </DialogPanel>
  </div>
</template>

<script lang="ts">
export default {
  name: 'LicenseUpdateDialog',
}
</script>

<script setup lang="ts">
  import type { ServiceContractInfo } from "@/types/accountApiDef"
  import { ref, computed, watch, onMounted, defineAsyncComponent } from 'vue'
  import { useRouter } from 'vue-router'
  import { isFree, isTrial } from '@/utils/kuraemon_plans'
  import { VueFinalModal } from 'vue-final-modal'
  import { formatTime } from '@/utils/datetimes'
  import { toUpperAlphaNum } from '@/utils/string_convert_procs'
  import LicenseCodeInput from './LicenseCodeInput.vue'
  import { externalLinks } from '@/utils/external_links'
  import { useAccountStore } from '@/stores/account'
  import { useLicenseInputStore } from "@/stores/licenseInput"
  import { useExternalTransitionStore } from "@/stores/externalTransition"

  import ValidationMessage from '@/components/ui/ValidationMessage.vue'
  import { useForm } from 'vee-validate'
  import { object, string } from 'yup';
  const accountStore = useAccountStore()
  const licenseInputStore = useLicenseInputStore()
  const externalTransitionStore = useExternalTransitionStore()
  const router = useRouter()

  // data
  const showModel = ref(false)
  const code1 = ref('')
  const code2 = ref('')
  const code3 = ref('')
  const code4 = ref('')

  const errorMessage = ref<{
    message: string
    displayContactSupportLink?: boolean
    displayPurchaseLicenseLink?: boolean
  }>({
    message: ''
  })
  const resultMessage = ref('')
  const acceptedPlan = ref<ServiceContractInfo | null>(null)
  // template ref
  const theLicenseUpdateCompleteDialog = ref<InstanceType<typeof DialogPanel>>();
  const theLicenseUpdateErrorDialog = ref<InstanceType<typeof DialogPanel>>();
  const licenseKey1 = ref<InstanceType<typeof LicenseCodeInput>>();
  const licenseKey2 = ref<InstanceType<typeof LicenseCodeInput>>();
  const licenseKey3 = ref<InstanceType<typeof LicenseCodeInput>>();
  const licenseKey4 = ref<InstanceType<typeof LicenseCodeInput>>();

  // components
  const DialogPanel = defineAsyncComponent(() => import("@/components/ui/DialogPanel.vue"))
  const LicensePromotionTextComponent = defineAsyncComponent(() => import("./LicensePromotionTextComponent.vue"))
  const ContactSupportLinkComponent = defineAsyncComponent(() => import("@/components/cloud/support/ContactSupportLinkComponent.vue"))
  // computed
  const licenseCode = computed(() => {
    return `${code1.value}-${code2.value}-${code3.value}-${code4.value}`;
  })
  const contractTerm = computed(() => {
    if (!acceptedPlan.value) return '−';
    const from = formatTime(acceptedPlan.value.service_contract_start_at, 'YYYY/M/D')
    const to = formatTime(acceptedPlan.value.service_contract_expired_at, 'YYYY/M/D')
    return `${from}~${to}`
  })
  const accountDetailInfo = computed(() => {
    return accountStore.accountDetailInfo;
  })
  const displayLicenseInput = computed(() => {
    return licenseInputStore.display
  })
  const fromLogin = computed(() => {
    return licenseInputStore.fromLogin
  })
  const isFreePlan = computed(() => {
    if (!accountDetailInfo.value || !accountDetailInfo.value.service_contract_type) return false; 
    return isFree(accountDetailInfo.value.service_contract_type)
  })
  const isTrialPlan = computed(() => {
    if (!accountDetailInfo.value || !accountDetailInfo.value.service_contract_type) return false;
    return isTrial(accountDetailInfo.value.service_contract_type)
  })
  onMounted(() => {
    if(displayLicenseInput.value){
      show();
    }
  })

  // vee-validateの設定
  const validationSchema = (() => {
    const regex = /[A-Z0-9]{4}/
    const codeValidation = string()
      .required("ライセンスコードの入力は必須です")
      .length(4, "4文字で入力してください")
      .matches(regex, "全角文字や記号は入力できません")
    return object({
      code1: codeValidation,
      code2: codeValidation,
      code3: codeValidation,
      code4: codeValidation,
    })
  })()
  const { errors, meta } = useForm({ validationSchema });

  const codesErrorMessage = computed(() => {
    const messages = Object.values(errors.value)
    if (messages.length < 1) return ""
    const uniq = [...new Set(messages)]
    return uniq[0]
  })

  // コードの一括ペースト中
  const bulkPasting = ref(false)

  // 次へフォーカスを移す
  const focusSettings = [
    {
      codeProc: () => code1.value,
      forcusProc: () => licenseKey2.value?.focusElement(),
    },
    {
      codeProc: () => code2.value,
      forcusProc: () => licenseKey3.value?.focusElement(),
    },
    {
      codeProc: () => code3.value,
      forcusProc: () => licenseKey4.value?.focusElement(),
    },
  ]
  focusSettings.forEach(focusSetting => {
    watch(
      focusSetting.codeProc,
      (newCode) => {
        if (newCode.length < 4) return;
        if (bulkPasting.value) return;
        focusSetting.forcusProc()
      },
    )
  })

  watch(
    () => displayLicenseInput.value,
    (displayLicenseInput) => {
      if(displayLicenseInput){
        show();
      }
    },
  )
  // methods
  const show = () => {
    code1.value = ''
    code2.value = ''
    code3.value = ''
    code4.value = ''
    showModel.value = true
  }
  const hide = () => {
    licenseInputStore.hide();
    showModel.value = false
  }
  const updateLicense = async () => {
    const args = {
      license_code: licenseCode.value,
      license_register_route: fromLogin.value ? 'after_login' : 'from_account_plan',
    }
    const result = await accountStore.registerAccountLicense(args);
    if(!result) return;
    if('license_error' in result){
      // ライセンスエラーの場合
      const errorMessages = {
        INVALID_LICENSE: { message: `入力されたライセンスコードは正しくありません
ご確認の上、再度ご入力ください` },
        EXPIRED_LICENSE: { message: '入力されたライセンスコードの期限が切れています' },
        EXCEEDED_PERIOD: { message: `蔵衛門クラウドプレミアムの
最大契約期間を超えるライセンスキーは
ご利用いただけません` },
        USED_LICENSE: { message: 'このライセンスコードは既に使われています' },
        EXCEEDED_COUNT: {
          message: `入力されたライセンスコードは
利用できるアカウント数の上限に達しています`,
          displayContactSupportLink: true,
        },
        INVALID_ACCOUNT_ADMIN_RULE_ENTERPRIZE: {
          message: `このライセンスコードは
          利用できるアカウントが制限されています
          お客様のアカウントではご利用いただけません`,
        },
        INVALID_ACCOUNT_BUSINESS_RULE_ENTERPRIZE: {
          message: `このライセンスコードはご利用いただけません
          お手数ですが、サポート窓口までご連絡ください`,
          displayContactSupportLink: true,
        },
        USED_LICENSE_ENTERPRIZE: {
          message: `すでにお客様のご利用プランは
          エンタープライズになっています`,
        },
        PACK_LICENSE_APPLIED_TO_SAME_LICENSED_ACCOUNT: {
          message: `同じパック内のライセンスコードは
          １つのアカウントに複数入力できません`,
          displayPurchaseLicenseLink: true,
        },
      }
      errorMessage.value = errorMessages[result.license_error];
      licenseInputStore.hide();
      showModel.value = false
      theLicenseUpdateErrorDialog.value?.show();
      return;
    }

    // 確定した次のプラン
    acceptedPlan.value = result.account_service_contract_info;
    resultMessage.value = (() => {
      if (isTrial(accountDetailInfo.value?.service_contract_type)) {
        return "契約内容の更新を受け付けました\n無料体験期間の終了後に次のプランへ移行します"
      }
      if (isFree(accountDetailInfo.value?.service_contract_type)) {
        return "契約内容の更新を受け付けました"
      }
      return "契約内容の延長を受け付けました"
    })()

    showModel.value = false
    theLicenseUpdateCompleteDialog.value?.show();
  }
  const closeWarningDialog = () => {
    theLicenseUpdateErrorDialog.value?.hide()
  }
  const licenseUpdateCompleted = () => {
    theLicenseUpdateCompleteDialog.value?.hide()
    router.go(0);
  }
  const onPaste1 = (ev: ClipboardEvent) => {
    const clipboardData = ev.clipboardData
    if(!clipboardData) return 

    // [-]でsplitする
    const pasteText = clipboardData.getData('text');
    if(!pasteText) return
    const codes = pasteText.split('-').map(
      s => (toUpperAlphaNum(s))
    )

    try {
      bulkPasting.value = true
      //コードを設定
      code1.value = codes[0].slice(0, 4)
      code2.value = codes[1] ? codes[1].slice(0, 4) : code2.value
      code3.value = codes[2] ? codes[2].slice(0, 4) : code3.value
      code4.value = codes[3] ? codes[3].slice(0, 4) : code4.value

    } finally {
      setTimeout(() => {
        if (codes.length === 1) licenseKey2.value?.focusElement()
        if (codes.length === 2) licenseKey3.value?.focusElement()
        if (2 < codes.length) licenseKey4.value?.focusElement()
        bulkPasting.value = false
      }, 1)
    }
  }
</script>

<style lang="scss" scoped>
.license-update-dialog-wrap {
  // ヘッダー
  .modal-header {
    position: relative;
    display: flex;
    justify-content: flex-end;
    .close-wrap {
      position: relative;
      // 閉じるボタン
      .close-button {
        background: url('@/assets/icons/close_gray.svg') center center no-repeat;
        border: none;
        position: absolute;
        width: 30px;
        height: 30px;
        cursor: pointer;
        padding: 0;
        top: 15px;
        right: 15px;
      }
    }
  }
  .modal-body {
    padding: 3rem 2rem 0 2rem;
    text-align: center;
    .account-name,
    .license-update-caption {
      font-size: 1.5rem;
    }
    .license-input-instruction {
      padding-top: 1rem;
      font-size: 1.14rem;
    }
    .license-key-input-wrap {
      display: inline-block;
      padding: 2.71rem 0 2.28rem 0;
      &.fromLogin {
        padding: 3.14rem 0 3.64rem 0;
      }
      :deep(input) {
        width: calc(25% - 1rem);
        max-width: 134px;
        margin: 0 0.5rem;
        padding: 0.15rem;
        border-radius: 6px;
        border: solid 1px #0E2A3B;
        font-size: 1.5rem;
        text-align: center;
      }
    }
  }
  .modal-footer {
    padding: 0 2rem 3.35rem 2rem;
    text-align: center;
    .ok-button-wrap {
      // OKボタン
      margin: auto;
      max-width: 214px;
      .ok-button {
        width: 100%;
        height: 43px;
        border-radius: 43px;
        cursor: pointer;
        border: none;
        font-size: 1.28rem;
        background-color: #1A2274;
        color: #FFF;
        &:disabled {
          background-color: #999;
        }
      }
    }
    .buy-license-key-link-wrap {
      // フリー、トライアル向け
      margin-top: 2.07rem;
      .buy-license-key-link {
        border: none;
        background: transparent;
        text-decoration: underline;
        color: #4FC8A3;
        font-size: 1.14rem;
      }
    }
    .buy-license-key-wrap {
      // プレミアム向け
      padding: 2.35rem 2rem 2.14rem 2rem;
      background-color: #F3F3F3;
      max-width: 700px;
      margin: 2.71rem auto 0.36rem auto;
      .buy-license-caption {
        color: #000;
        font-size: 1.28rem;
        .emphasis {
          font-weight: bold;
        }
      }
      .buy-license-key-button {
        width: 100%;
        max-width: 362px;
        height: 42px;
        border-radius: 42px;
        cursor: pointer;
        border: none;
        background-color: #1A2274;
        color: #FFF;
        font-size: 1.28rem;
        margin-top: 1.28rem;
      }
    }
  }
  .license-update-error-dialog-message {
    text-align: center;
    margin: 3rem 0;
    .error-message {
      font-size: 1.3rem;
    }
    .error-description {
      margin-top: 2rem;
      font-size: 1.07rem;
    }
  }
  .footer-link-wrap {
    margin-top: 1rem;
    margin-bottom: -1rem;
    text-align: center;
    .footer-buy-license-key-link {
      border: none;
      background: transparent;
      text-decoration: underline;
      color: #4FC8A3;
      font-size: 1.14rem;
    }
  }
  .license-update-complete-dialog-message {
    text-align: center;
    margin: 1rem 0 0 0;
    .message {
      font-size: 1.5rem;
    }
    .contract-wrap {
      margin: 1rem 1.8rem 0;
      padding: 1.5rem 2rem;
      text-align: left;
      font-size: 1.2rem;
      background-color: #E9E9E9;
      border-radius: 5px;
      
      .contract-text {
        display: inline-block;
        line-height: 2rem;
        
        .plan-name-prefix
        , .contract-term-prefix {
          color: #707070;
        }
      }
    }
    .contract-term-link-wrap{
      margin-top: 1.5rem;

      .contract-term-link{
        color: #4FC8A3;
        font-size: 1rem;
      }
    }
    .button-wrap {
      display: inline-block;
      margin-top: 1.5rem;
      width: 100%;
      max-width: 214px;
      height: 43px;
      .ok-button {
        border: none;
        cursor: pointer;
        width: 100%;
        height: 100%;
        border-radius: 43px;
        font-size: 1.28rem;
        color: #FFF;
        background-color: #1A2274;
      }
    }
  }
  @media screen and (max-width: 800px) {
    .modal-body {
      .license-key-input-wrap input {
        width: calc(25% - 0.3rem);
        margin: 0 0.15rem;
      }
    }
  }
  @media screen and (max-width: 600px) {
    .modal-footer {
      .ok-cancel-wrap {
        .cancel-button-wrap,
        .ok-button-wrap {
          width: 100%;
          margin: auto;
        }
      }
    }
  }
}
</style>
<style lang="scss">
.license-update-dialog-wrap {
  .overlay-class{
    display: block !important;
  }
  .modal-container {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow-y: auto;
  }
  .modal-content {
    position: relative;
    display: flex;
    flex-direction: column;
    border: solid 1px #ccc;
    border-radius: 10px;
    background: #FFF;
  }
}
</style>